import { UiVaultConfig, VaultStats } from '../../@types/vaults';
import { CAPACITY_WARNING_THRESHOLD } from '../../constants/vaults/misc';
import MarketIcon from '../Utils/MarketIcon';
import { UIMarket } from '@velocity-exchange/common';
import { VaultCapacityProgressBar } from './VaultCapacityProgressBar';
import { WithClassName } from 'src/utils/UtilityTypes';
import { cj, cm } from 'src/utils/classNames';
import { Warning } from '@velocity-exchange/icons';
import { useWindowSizeIsLessThan } from 'src/hooks/useWindowSize';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import Tag from 'src/components/primitives/Tag/Tag';

const TagComponent = ({
	isFull,
	capacityPct,
	inDesktopRow = false,
}: {
	isFull: boolean;
	capacityPct: number;
	inDesktopRow?: boolean;
}) => {
	const isTabletSize = useWindowSizeIsLessThan('md');
	const label = isFull
		? 'Full'
		: capacityPct >= CAPACITY_WARNING_THRESHOLD
			? 'Almost Full'
			: '';

	const iconOnly = inDesktopRow && isTabletSize;

	if (!isFull && capacityPct < CAPACITY_WARNING_THRESHOLD) {
		return null;
	}

	return (
		<Tooltip content={label} disabled={!iconOnly}>
			<Tag
				className={cj(
					isFull
						? 'bg-negative-red-secondary-bg text-text-negative-red-button'
						: 'bg-warn-orange-secondary-bg text-star-yellow'
				)}
			>
				{iconOnly ? <Warning size={12} /> : label}
			</Tag>
		</Tooltip>
	);
};

export const VaultTvlProgressDisplay = ({
	vaultStats,
	uiVaultConfig,
	className,
	inDesktopRow = false,
}: {
	vaultStats: VaultStats;
	uiVaultConfig: UiVaultConfig;
	inDesktopRow?: boolean;
} & WithClassName) => {
	const tvlQuoteNum = vaultStats.tvlQuote.toNum();
	const pct = vaultStats.capacityPct;
	const safePct = pct > 0 ? pct : Number.EPSILON;
	const totalCapacityQuote = tvlQuoteNum * (100 / safePct);
	const remainingCapacityQuote = Math.max(0, totalCapacityQuote - tvlQuoteNum);
	const isFull = remainingCapacityQuote <= 1000;

	return (
		<div
			className={cm(
				'flex w-full flex-col items-start justify-center gap-sm',
				className
			)}
		>
			<div className="flex w-full justify-between">
				<div className="flex items-end gap-1">
					<MarketIcon
						marketSymbol={
							UIMarket.createSpotMarket(uiVaultConfig.depositAsset).market
								.symbol
						}
						className="size-4"
					/>
					<span className="typo-label-sm text-text-default">
						{vaultStats.tvlBase.toMillified()}
					</span>
					<span className="typo-body-xs text-text-tertiary">
						(${vaultStats.tvlQuote.toMillified()})
					</span>
				</div>
				<div>
					<TagComponent
						isFull={isFull}
						capacityPct={vaultStats.capacityPct}
						inDesktopRow={inDesktopRow}
					/>
				</div>
			</div>

			<VaultCapacityProgressBar capacityPct={vaultStats.capacityPct} />
		</div>
	);
};
